import { Flex, Stack, Text } from '@chakra-ui/react'
import { useCallback } from 'react'

import useIsVisible from '@/client/hooks/useIsVisible'

import useRealtimeNotifications from '@/components/Worlds/hooks/user/useRealtimeNotifications'
import useRealtimeUserNotificationDetails from '@/components/Worlds/hooks/user/useRealtimeUserNotificationDetails'

import NotificationItem from './internal/NotificationItem'
import NotificationsLoading from './internal/NotificationsLoading'

export default function NotificationItems({
  onClickNotification,
  isOpen,
}: {
  onClickNotification?: () => void
  isOpen: boolean
}): JSX.Element | null {
  const skipSubscription = !isOpen

  const {
    notifications,
    hasMore,
    loading: loadingNotifications,
    onLoadMore,
  } = useRealtimeNotifications({ skip: skipSubscription })

  const { data } = useRealtimeUserNotificationDetails({
    skip: skipSubscription,
  })

  const onVisible = useCallback(() => {
    if (hasMore && !loadingNotifications) {
      onLoadMore()
    }
  }, [hasMore, loadingNotifications, onLoadMore])

  const [scrollRef] = useIsVisible<HTMLDivElement>({ onVisible })

  const isInitialLoad = notifications == null || data == null
  const isLoadingMore = loadingNotifications != null && hasMore
  const showSpinner = isInitialLoad || isLoadingMore

  return (
    <Stack direction="column" align="center" spacing="8px">
      {isInitialLoad
        ? null
        : notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              lastViewedAt={data.notificationsLastViewedAt}
              onClick={onClickNotification}
            />
          ))}
      <Flex justify="center" width="100%" ref={scrollRef}>
        {showSpinner ? (
          <NotificationsLoading />
        ) : notifications.length === 0 ? (
          <Text color="fg.muted" variant="supporting">
            No notifications right now!
          </Text>
        ) : !hasMore ? (
          <Text color="fg.muted" variant="supporting">
            That&apos;s it for now!
          </Text>
        ) : null}
      </Flex>
    </Stack>
  )
}
